import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Grid, Chip } from '@mui/material';
import { ibpJobRunsListing, ibpJobSearch } from '../../service/service';
import { CustomizedDataGrid } from '../customized_data_grid';
import moment from 'moment-timezone';
import type { Column } from 'react-data-grid';
import './ibp-obs.scss';
import Loader from '../Loader/loader';

import { GDOList } from '../common-util';

const IBPExecutedJobs = ({ gdoName , marketName }: any) => {
	interface Row {
		runStatus: string;
		commonStatus: string;
		reasonForDelay: string;
		actionTaken: string;
		ticketNumber: string;
		userStatus: string;
		downstream: string;
		slaStatus: string;
		_id: string;
		name: string;
		jobId: string;
		status: string;
		exitCode: string;
		runNum: string;
		application: string;
		subApplication: string;
		runStartTime: string;
		runEndTime: string;
		slaEndTime: string;
		criticalFlag: boolean;
		machine: string;
		createdAt: string;
		updatedAt: string;
		updatedBy: string;
		boxName: string;
		jobType: string;
		children?: Row[];
		ischildren?: boolean;
		isExpanded?: boolean;
		actualDuration: string;
		overRun: boolean;
		thresholdPercent: string;
		durationDelay: string;
		avgRunTime: number;
		estimatedEndTime: string;
		totalRuns: number;
	}

	const renderStatus = (status: any) => {
		switch (status) {
			case 'SUCCESS':
			case 'Successful':
			case 'COMPLETED':
				return <Chip label={status} color="success" />;
			case 'FAILURE':
			case 'Failure':
			case 'TERMINATED':
			case 'Terminated':
			case 'SUSPENDED':
				return <Chip label={status} color="secondary" />;
			case 'NOT STARTED':
			case 'Not Started':
				return <Chip label={status} style={{ backgroundColor: '#1B1E35CC', color: '#71758F' }} />;
			case 'RUNNING':
			case 'ACTIVATED':
			case 'In Progress':
			case 'Hold':
				return <Chip label={status} style={{ backgroundColor: '#299BFF26', color: '#299BFF' }} />;
			default:
				return <Chip label={status} style={{ backgroundColor: '#299BFF26', color: '#299BFF' }} />;
		}
	};

	function getColumns(): Array<Column<Row>> {
		return [
			{
				key: 'name',
				name: 'Job',
				width: 440,
				minWidth: 440,
			},
			{
				key: 'source',
				name: 'Source',
				width: 200,
				minWidth: 200
			},
			{   
				key: 'destination',
				name: 'Destination',
				width: 200,
				minWidth: 200
			},
			{
				key: 'runStartTime',
				name: 'Start Time',
				width: 200,
				minWidth: 200,
				formatter(props) {
					const startDate = props.row.runStartTime;
					if (startDate) {
						return `${moment().subtract(1,'day').format('MM-DD-YY')} ${moment(startDate).format('HH:mm:ss')}`;
					}
					return '';
				}
			},
			{
				key: 'runEndTime',
				name: 'End Time',
				width: 200,
				minWidth: 200,
				formatter(props) {
					const endDate = props.row.runEndTime;
					if (endDate) {
						return `${moment().subtract(1,'day').format('MM-DD-YY')} ${moment(endDate).format('HH:mm:ss')}`;
					}
					return '';
				}
			},
			{
				key: 'runStatus',
				name: 'Job Status',
				width: 200,
				minWidth: 200,
				formatter(props) {
					const status = props.row.runStatus;
					return renderStatus(status);
				}
			}
		];
	}

	const defaultColumnNames = [
		'Job',
		'Source',
		'Destination',
		'Start Time',
		'End Time',
		'Job Status'
	];
	const dataFetchedRef = useRef(false);
	const [jobsList, setJobsList] = useState<Row[]>([]);
	const [loading, setLoading] = useState(false);
	const [paginationData, setPaginationData] = useState({ totalElements: 0, totalPages: 0 });
	const [page, setPage] = useState(1);
	const [pageSize, setPageSize] = useState(100);
	const gdoListRef = useRef<any[]>([]);

	const [sortingInfo, setSortingInfo] = useState({ sortBy: '', sortDir: '' });
	const [filterData, setFilterData] = useState<Row[]>([]);
	const [filterInfo, setFilterInfo] = useState<Record<string, any>>({});
	const [searchDetails, setSearchDetails] = useState([]);
	const [searchParams, setSearchParams] = useSearchParams();
	const hasDrillDowned = searchParams.has('isDrillDowned');

    
	moment.updateLocale(moment.locale(), { invalidDate: '' });

	const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
		jobListingFunc(value, pageSize, sortingInfo, parseFilter(filterInfo));
	};

	const handlepageSizeChange = (event: any) => {
		jobListingFunc('1', event.target.value, sortingInfo, parseFilter(filterInfo));
	};
	const handleSorting = (sortingInfo: any) => {
		jobListingFunc(page, pageSize, sortingInfo, parseFilter(filterInfo));
	};
	const resetSearchParams = () => {
		if (hasDrillDowned) {
			searchParams.delete('status');
			searchParams.delete('isDrillDowned');
			setSearchParams(searchParams);
		}
	};

	const handleFilter = (filterInfo: any) => {
		console.log(filterInfo)
		resetSearchParams();
		setFilterInfo(filterInfo);
		jobListingFunc(1, pageSize, sortingInfo, parseFilter(filterInfo));
	};
	const handleSearch = (searchDetails: any, columnKey: any) => {
		jobSearchFunc(searchDetails, parseFilter(filterInfo, columnKey), columnKey);
	};

	const parseFilter = (filterInfo: any, name?: string) => {
		// Call only filterable columns and reset the filtering in case of wrong/empty column
		const parsedFilter: any = {};
		Object.keys(filterInfo).forEach((key: any) => {
			if (key !== name && filterInfo[key]?.length) {
				parsedFilter[key] = filterInfo[key];
			}
		});
		return parsedFilter;
	};

	const getFilterQuerySkeleton = () => {
			const filterQuerySkeleton: Record<string, any> = {};

			if (!hasDrillDowned) {
				getColumns().forEach((column: any) => {
					filterQuerySkeleton[column.key] = [];
				});
				return filterQuerySkeleton;
			}

			const status = searchParams.get('status');

			const startDaily = moment(new Date())
				.startOf('day')
				.subtract(1, 'hours')
				.format('YYYY-MM-DD HH:mm');

			const endDaily = moment(new Date())
				.startOf('day')
				.endOf('day')
				.subtract(1, 'hours')
				.format('YYYY-MM-DD HH:mm');

			const startDt = moment(searchParams.get('fromDate'))
				.startOf('day')
				.subtract(1, 'hours')
				.format('YYYY-MM-DD HH:mm');

			const endDt = moment(searchParams.get('toDate'))
				.endOf('day')
				.subtract(1, 'hours')
				.format('YYYY-MM-DD HH:mm');

			switch (status) {
				case 'Critical':
					filterQuerySkeleton.criticalFlag = ['true'];
					break;
				case 'Non Critical':
					filterQuerySkeleton.criticalFlag = ['false'];
					break;
				case 'New Jobs':
					filterQuerySkeleton.createdAt = [startDaily, endDaily];
					filterQuerySkeleton.criticalFlag = ['true'];
					break;
				case 'Non Critical New Jobs':
					filterQuerySkeleton.createdAt = [startDaily, endDaily];
					filterQuerySkeleton.criticalFlag = ['false'];
					break;
				case 'Critical Exceed Estimated Duration Trend':
					filterQuerySkeleton.criticalFlag = ['true'];
					filterQuerySkeleton.execExceedEstimatedDurationTrend = ['Yes'];
					break;
				case 'Critical Exceed Threshold Trend':
					filterQuerySkeleton.criticalFlag = ['true'];
					filterQuerySkeleton.execExceedThresholdTrend = ['Yes'];
					break;
				case 'Critical Failed Trend':
					filterQuerySkeleton.criticalFlag = ['true'];
					filterQuerySkeleton.execFailedTrend = ['Yes'];
					break;
				case 'Non Critical Exceed Estimated Duration Trend':
					filterQuerySkeleton.criticalFlag = ['false'];
					filterQuerySkeleton.execExceedEstimatedDurationTrend = ['Yes'];
					break;
				case 'Non Critical Exceed Threshold Trend':
					filterQuerySkeleton.criticalFlag = ['false'];
					filterQuerySkeleton.execExceedThresholdTrend = ['Yes'];
					break;
				case 'Non Critical Failed Trend':
					filterQuerySkeleton.criticalFlag = ['false'];
					filterQuerySkeleton.execFailedTrend = ['Yes'];
					break;
				case 'New Jobs Graph':
					filterQuerySkeleton.createdAt = [startDt, endDt];
					filterQuerySkeleton.criticalFlag = ['true'];
					break;

				case 'Non Critical New Jobs Graph':
					filterQuerySkeleton.createdAt = [startDt, endDt];
					filterQuerySkeleton.criticalFlag = ['false'];
					break;
			}
			return filterQuerySkeleton;
		};

	const getStoredJobState = () => {
		if (!localStorage.getItem('selectedAlert')) {
			return null;
		}

		const stored = localStorage.getItem('alertJobState');
		localStorage.removeItem('alertJobState');

		return stored ? JSON.parse(stored) : null;
	};

	const loadJobs = (
		filterQuerySkeleton: any,
		storedJobState: any
	) => {
		if (hasDrillDowned) {
			jobListingFunc(
				page,
				pageSize,
				sortingInfo,
				parseFilter(filterQuerySkeleton)
			);
			return;
		}

		if (storedJobState?.pageNumber && storedJobState.pageSize) {
			if (
				localStorage.getItem('overviewPageHit') &&
				storedJobState.jobs
			) {
				localStorage.removeItem('overviewPageHit');
				setJobsList(storedJobState.jobs);
			} else {
				jobListingFunc(
					1,
					storedJobState.pageSize,
					storedJobState.sortingInfo,
					parseFilter(storedJobState.filterInfo),
					gdoName
				);
			}

			setPaginationData(storedJobState.page);
			setFilterData(storedJobState.filterOptions);
			setPage(1);
			setPageSize(storedJobState.pageSize);
			setSortingInfo(storedJobState.sortingInfo);
			setFilterInfo(storedJobState.filterInfo);
			return;
		}

		jobListingFunc(
			page,
			pageSize,
			sortingInfo,
			parseFilter(filterQuerySkeleton),
			gdoName
		);
	};

	const updateBodyClass = () => {
		const url = globalThis.location.pathname;
		const activeId = document.getElementById('root');
		const jobName = url.substring(url.lastIndexOf('/') + 1);

		if (
			url === '/jobs' ||
			(jobName !== url && url === `/jobs/${jobName}` && activeId)
		) {
			document.body.classList.add('jobs-list-datas');
			document.body.classList.remove('jobs-exe-datas');
		}
	};

	useEffect(() => {
		gdoListRef.current = GDOList();

		if (dataFetchedRef.current) return;
		dataFetchedRef.current = true;

		const filterQuerySkeleton = getFilterQuerySkeleton();
		setFilterInfo(filterQuerySkeleton);

		const storedJobState = getStoredJobState();

		loadJobs(filterQuerySkeleton, storedJobState);

		updateBodyClass();
	}, [gdoName]);

	async function jobSearchFunc(searchDetails: any, jobFilterInfo: any, columnKey: any) {
		const res = await ibpJobSearch(searchDetails, jobFilterInfo, columnKey);
		if (res.success) {
			setSearchDetails(res.data);
		}
	}

	async function jobListingFunc(pageNumber: any, size: any, sortingInfo: any, jobFilterInfo: any, gdoName?: string) {
		setLoading(true);
		if (pageSize !== size) {
			pageNumber = 1;
		}
		const res = await ibpJobRunsListing(pageNumber.toString(), size.toString(), sortingInfo, jobFilterInfo, gdoName, marketName);
		if (res.success) {
			setJobsList(res.data.jobs);
			setTimeout(() => {
				const table = document.getElementById(
					`ibp_jobs_tabledata_${gdoName?.replace(' ', '_')}`
				);

				if (table) {
					const rdgElement = table.querySelector<HTMLElement>('.rdg');
						if (rdgElement) {
							const jobsLength = res.data.jobs.length;
							rdgElement.style.height = `${
								jobsLength === 1 ? jobsLength * 80 : jobsLength * 55
							}px`;
					}
				}
			}, 100);
			setPaginationData(res.data.page);
			setFilterData(res.data.filterOptions);
			setPage(pageNumber);
			setPageSize(size);
			setSortingInfo(sortingInfo);
			setLoading(false);
			if (!localStorage.getItem('selectedAlert')) {
				const ibp_jobState = {
					jobs: res.data.jobs,
					page: res.data.page,
					filterOptions: res.data.filterOptions,
					pageSize: size,
					pageNumber,
					sortingInfo,
					filterInfo: jobFilterInfo
				};
				localStorage.setItem('ibp_jobState', JSON.stringify(ibp_jobState));
			}
			localStorage.removeItem('selectedAlert');
		}
	}



	return (
		<div style={{marginTop: 0, marginRight: 16, width: '99%'}} className="page-table ibp_jobs_tabledata jobs_tabledata page-table-ibp-obs" id={`ibp_jobs_tabledata_${gdoName.replace(' ', '_')}`}>
			<Grid>
				{ loading ? (
					<Loader />
				) : (
					<CustomizedDataGrid
						tableColumns={getColumns()}
						tableData={jobsList}
						tableType="IBP_JobRuns"
						defaultColumnNames={defaultColumnNames}
						onSorting={handleSorting}
						currSorting={sortingInfo}
						filterDataOptions={filterData}
						searchDataValues={searchDetails}
						onFilterCheck={handleFilter}
						currFilter={filterInfo}
						onFilterSearch={handleSearch}
						page={page}
						pageSize={pageSize}
						paginationData={paginationData}
						listLength={jobsList.length}
						handlepageSizeChange={handlepageSizeChange}
						handlePageChange={handlePageChange}
						isFetching={loading}
						showExport={true}
						fileName={`Jobs.xlsx`}
						isDateRangeFilter={false}
						isDisabled={false}
					/>
				)}
			</Grid>
		</div>
	);
}
export default IBPExecutedJobs;